import React from 'react';
import { isMarketOpen, isMarketHoliday, getHolidayName, getLastTradingDay, isPreMarket } from '../../utils/marketHours';
import '../../styles/features/dataFreshness.css';

interface DataFreshnessIndicatorProps {
  lastUpdated: string | null | undefined;
  type: 'stock' | 'option';
}

/**
 * DataFreshnessIndicator Component
 *
 * Displays a colored dot indicator showing the freshness of market data:
 * - Green: Market open + price updated within 65 minutes (live)
 * - Orange: Market open + price stale (> 65 minutes)
 * - Grey: Market closed
 *
 * Shows timestamp in tooltip on hover.
 */
const DataFreshnessIndicator: React.FC<DataFreshnessIndicatorProps> = ({ lastUpdated, type }) => {
  // Determine freshness level based on market status
  let freshnessClass = '';
  let tooltipText = '';
  const marketOpen = isMarketOpen();
  const typeLabel = type === 'stock' ? 'Stock' : 'Option';

  // Format timestamp helper (full with time)
  const formatTimestamp = (dateStr: string): string => {
    const date = new Date(dateStr);
    const formattedDate = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    return `${formattedDate} at ${formattedTime}`;
  };

  // Format date only helper (for last trading day)
  const formatDateOnly = (dateStr: string): string => {
    const date = new Date(dateStr + 'T12:00:00'); // Add time to avoid timezone issues
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const preMarket = isPreMarket();

  if (!marketOpen && !preMarket) {
    // Market closed (and not pre-market) - always show grey
    freshnessClass = 'freshness-grey';
    const holidayName = getHolidayName();
    const closedReason = holidayName ? `${holidayName}` : 'Market Closed';
    const lastTradingDay = getLastTradingDay();

    if (lastTradingDay) {
      // Show "Data from [last trading day]" - this is more accurate than showing today's date
      tooltipText = `${closedReason} · Data from ${formatDateOnly(lastTradingDay)}`;
    } else if (lastUpdated) {
      // Fallback to showing last update time if we don't have last trading day info
      tooltipText = `${closedReason} · Last update: ${formatTimestamp(lastUpdated)}`;
    } else {
      tooltipText = `${closedReason} · No recent ${typeLabel.toLowerCase()} data`;
    }
  } else if (!lastUpdated) {
    // Market open but no data
    freshnessClass = 'freshness-red';
    tooltipText = `${typeLabel} price: No data available`;
  } else {
    // Market open with data - check freshness
    const lastUpdatedDate = new Date(lastUpdated);
    const now = new Date();
    const ageInMinutes = (now.getTime() - lastUpdatedDate.getTime()) / (1000 * 60);

    const sessionLabel = preMarket ? 'Pre-Market' : 'Live';

    if (ageInMinutes < 65) {
      freshnessClass = 'freshness-green';
      tooltipText = `${typeLabel} price: ${sessionLabel} · Updated ${formatTimestamp(lastUpdated)}`;
    } else {
      freshnessClass = 'freshness-orange';
      tooltipText = `${typeLabel} price: Stale · Last update: ${formatTimestamp(lastUpdated)}`;
    }
  }

  return (
    <div className="data-freshness-indicator" data-tooltip={tooltipText}>
      <span className={`freshness-dot ${freshnessClass}`}></span>
    </div>
  );
};

export default DataFreshnessIndicator;
